import { useState, useEffect, useRef } from 'react';
import { Box, Skeleton } from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';

// Base64 encoded SVG placeholder - prevents flickering when no image is available
const DEFAULT_PLACEHOLDER = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiB2aWV3Qm94PSIwIDAgMjAwIDIwMCI+PHJlY3QgZmlsbD0iI2YwZjBmMCIgd2lkdGg9IjIwMCIgaGVpZ2h0PSIyMDAiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iI2JkYmRiZCIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjE0Ij5ObyBJbWFnZTwvdGV4dD48L3N2Zz4=';

/**
 * LazyImage Component - Loads images only when they enter the viewport
 *
 * @param {string} src - Image URL
 * @param {string} alt - Alt text
 * @param {object} sx - MUI sx props for styling
 * @param {number} height - Fixed height for skeleton loader
 * @param {string} fallbackSrc - Fallback image if main image fails
 * @param {function} component - Component to render (default: 'img')
 * @param {boolean} showPlaceholderIcon - Show icon placeholder instead of image when no src
 * @param {object} ...props - Additional props passed to the component
 */
const LazyImage = ({
    src,
    alt,
    sx = {},
    height,
    fallbackSrc = DEFAULT_PLACEHOLDER,
    component = 'img',
    showPlaceholderIcon = false,
    ...props
}) => {
    const [imageSrc, setImageSrc] = useState(null);
    const [imageLoaded, setImageLoaded] = useState(false);
    const imgRef = useRef(null);

    // Check if we should show the placeholder icon (no src and showPlaceholderIcon is true)
    const shouldShowPlaceholderIcon = !src && showPlaceholderIcon;

    useEffect(() => {
        // Reset state when src changes
        setImageSrc(null);
        setImageLoaded(false);

        if (!src) {
            // Use inline placeholder immediately to prevent flickering
            setImageSrc(fallbackSrc);
            setImageLoaded(true);
            return;
        }

        // Store current ref value for cleanup
        const currentImgRef = imgRef.current;

        // IntersectionObserver to detect when image enters viewport
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        // Start loading the image
                        setImageSrc(src);
                        // Disconnect observer after image starts loading
                        if (currentImgRef) {
                            observer.unobserve(currentImgRef);
                        }
                    }
                });
            },
            {
                // Start loading when image is 200px away from viewport
                rootMargin: '200px',
                threshold: 0.01
            }
        );

        if (currentImgRef) {
            observer.observe(currentImgRef);
        }

        return () => {
            if (currentImgRef) {
                observer.unobserve(currentImgRef);
            }
        };
    }, [src, fallbackSrc]);

    const handleLoad = () => {
        setImageLoaded(true);
    };

    const handleError = () => {
        setImageSrc(fallbackSrc);
        setImageLoaded(true);
    };

    // Render icon placeholder for no image scenario
    if (shouldShowPlaceholderIcon) {
        return (
            <Box
                ref={imgRef}
                sx={{
                    position: 'relative',
                    width: '100%',
                    height: height || 200,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f5f5f5',
                    borderRadius: sx.borderRadius || 1,
                    ...sx
                }}
            >
                <ImageIcon sx={{ fontSize: 48, color: '#bdbdbd' }} />
            </Box>
        );
    }

    return (
        <Box
            ref={imgRef}
            sx={{
                position: 'relative',
                width: '100%',
                height: height || 'auto',
                ...sx
            }}
        >
            {!imageLoaded && (
                <Skeleton
                    variant="rectangular"
                    width="100%"
                    height={height || '100%'}
                    sx={{
                        position: imageSrc ? 'absolute' : 'relative',
                        top: 0,
                        left: 0,
                        borderRadius: sx.borderRadius || 0
                    }}
                    animation="wave"
                />
            )}
            {imageSrc && (
                <Box
                    component={component}
                    src={imageSrc}
                    alt={alt}
                    onLoad={handleLoad}
                    onError={handleError}
                    sx={{
                        width: '100%',
                        height: '100%',
                        opacity: imageLoaded ? 1 : 0,
                        transition: 'opacity 0.3s ease-in-out',
                        ...sx
                    }}
                    {...props}
                />
            )}
        </Box>
    );
};

export default LazyImage;
export { DEFAULT_PLACEHOLDER };
